import React, { forwardRef } from 'react';
import { GridFilterItemType } from '../../Enums/GridFilterItemType.enum';
import FormControl from '../../../../Components/Form/FormControl';
import DatePicker from '../../../../Components/Base/DatePicker/DatePicker';
import PatientSelect from '../../../Patient/Components/PatientSelect/PatientSelect';
import DoctorSelect from '../../../Doctor/Components/DoctorSelect/DoctorSelect';
import DoctorAsyncSelect from '../../../Doctor/Components/DoctorAsyncSelect/DoctorAsyncSelect';
import QuestionnaireSelect from '../../../Questionnaire/Components/QuestionnaireSelect/QuestionnaireSelect';
import Select from '../../../../Components/Base/Select/Select';
import { WorkplacePatientStatus } from '../../../EArchive/Enums/WorkplacePatientStatus.enum';
import HealthPlanMultiSelect from '../../../HealthPlan/Components/HealthPlanMultiSelect/HealthPlanMultiSelect';
import WorkplaceCaseTypeMultiSelectWithCheckList from '../../../Workplace/Components/WorkplaceCaseTypeMultiSelectWithCheckList/WorkplaceCaseTypeMultiSelectWithCheckList';
import DoctorMultiSelectWithCheckList from '../../../Doctor/Components/DoctorMultiSelectWithCheckList/DoctorMultiSelectWithCheckList';
import WorkplaceMultiSelectWithCheckList from '../../../Workplace/Components/WorkplaceMultiSelectWithCheckList/WorkplaceMultiSelectWithCheckList';
import CasePrioritySelect from '../../../Term/Components/CasePrioritySelect/CasePrioritySelect';
import { NCZIElectronicExaminationRecordType } from '../../../Nczi/Enums/ncziElectronicExaminationRecordType.enum';
import MultiSelect from '../../../../Components/Base/MultiSelect/MultiSelect';
import WorkplaceSelect from '../../../Workplace/Components/WorkplaceSelect/WokrplaceSelect';
import TextField from '../../../../Components/Base/TextField/TextField';
import RootCaseTypeSelect from '../../../CaseType/Components/RootCaseTypeSelect/rootCaseTypeSelect';
import RoleMultiSelectWithCheckList from '../Inputs/RoleMultiSelectWithCheckList/RoleMultiSelectWithCheckList';
import InsuranceCompaniesMultiSelectWithCheckList from '../Inputs/InsuranceCompaniesMultiSelectWithCheckList/InsuranceCompaniesMultiSelectWithCheckList';
import QuestionnaireCodeSelect from '../../../Questionnaire/Components/QuestionnaireCodeSelect/QuestionnaireCodeSelect';
import InputField from '../../../../Components/Form/components/InputField';
import PatientAsyncSelect from '../../../Patient/Components/PatientAsyncSelect/PatientAsyncSelect';

export interface GridFilterItemProps {
	type: GridFilterItemType;
	width?: number;
	contentWidth?: number;
	customLabel?: string;
	customPlaceholder?: string;
	customOnChange?: (item: any) => void;
	enableAutocomplete?: boolean;
	customKey?: string;
	visible?: boolean;
	formControlClassName?: string;
}

const ITEMS = {
	[GridFilterItemType.CREATED_AT]: {
		key: 'createdAt',
		label: 'base.shared.date',
		component: <DatePicker />,
	},
	[GridFilterItemType.DATE_FROM]: {
		key: 'dateFrom',
		label: 'base.shared.dateFrom',
		component: <DatePicker usePortal />,
	},
	[GridFilterItemType.DATE_TO]: {
		key: 'dateTo',
		label: 'base.shared.dateTo',
		component: <DatePicker usePortal />,
	},
	[GridFilterItemType.PATIENT_ID]: {
		key: 'patient',
		label: 'base.shared.patient',
		component: <PatientAsyncSelect />,
	},
	[GridFilterItemType.PATIENT_ORIGINAL_ID]: {
		key: 'patient',
		label: 'base.shared.patient',
		component: <PatientAsyncSelect useOriginalId />,
	},
	[GridFilterItemType.PATIENT_BIRTH_NUMBER]: {
		key: 'patient',
		label: 'base.shared.patient',
		component: <PatientAsyncSelect useBirthNumber />,
	},
	[GridFilterItemType.PATIENT_ID_FROM_ALL_WORKPLACES]: {
		key: 'patient',
		label: 'base.shared.patient',
		component: <PatientAsyncSelect searchInAllWorkplaces />,
	},
	[GridFilterItemType.PATIENT_ORIGINAL_ID_FROM_ALL_WORKPLACES]: {
		key: 'patient',
		label: 'base.shared.patient',
		component: <PatientAsyncSelect searchInAllWorkplaces useOriginalId />,
	},
	[GridFilterItemType.DOCTOR_ID]: {
		key: 'doctor',
		label: 'base.shared.doctor',
		component: <DoctorSelect />,
	},
	[GridFilterItemType.DOCTOR_ID_PREFETCH]: {
		key: 'doctor',
		label: 'base.shared.doctor',
		component: <DoctorAsyncSelect />,
	},
	[GridFilterItemType.DOCTOR_ID_FROM_ALL_WORKPLACES]: {
		key: 'doctor',
		label: 'base.shared.doctor',
		component: <DoctorSelect searchInAllWorkplaces />,
	},
	[GridFilterItemType.DOCTOR_IDS]: {
		key: 'doctorIds',
		label: 'base.shared.doctor',
		component: <DoctorMultiSelectWithCheckList portal />,
	},
	[GridFilterItemType.DOCTOR_IDS_NURSE_CASES_CUSTOM]: {
		key: 'doctorIds',
		label: 'base.shared.doctor',
		component: (
			<DoctorMultiSelectWithCheckList
				portal
				allowNotSpecified
				onlyDoctorsAndMe
			/>
		),
	},
	[GridFilterItemType.QUESTIONNAIRE_ID]: {
		key: 'questionnaire',
		label: 'base.shared.questionnaire',
		component: <QuestionnaireSelect />,
	},
	[GridFilterItemType.QUESTIONNAIRE_CODE]: {
		key: 'questionnaireCode',
		label: 'base.shared.questionnaire',
		component: <QuestionnaireCodeSelect />,
	},
	[GridFilterItemType.QUESTIONNAIRE_RESULT]: {
		key: 'questionnaireResult',
		label: 'base.urgency',
		component: (
			<Select
				options={[
					{ value: null, label: 'base.everything' },
					{
						value: 'SIGNIFICANT_RISK_OF_SUICIDE',
						label: 'component.questionnaires.highRiskResult',
					},
					{
						value: 'SIGNIFICANT_RISK_OF_MENTAL_DECOMPENSATION',
						label: 'component.questionnaires.mediumRiskResult',
					},
					{ value: 'NO_RISK', label: 'base.noRisk' },
				]}
				translate
			/>
		),
	},
	[GridFilterItemType.CASE_STATUS]: {
		key: 'caseStatus',
		label: 'base.requests',
		component: (
			<Select
				options={[
					{ value: null, label: 'base.everything' },
					{
						value: 'NEW',
						label: 'base.shared.new',
					},
					{
						value: 'IN_PROGRESS',
						label: 'base.notFinished',
					},
				]}
				translate
				firstUpperValues
			/>
		),
	},
	[GridFilterItemType.CREATED_STATUS]: {
		key: 'createdStatus',
		label: 'base.shared.date',
		component: (
			<Select
				options={[
					{ value: null, label: 'base.everything' },
					{
						value: 'older_16h',
						label: 'base.shared.older16Hours',
					},
				]}
				translate
			/>
		),
	},
	[GridFilterItemType.CASE_TYPE]: {
		key: 'caseTypeIds',
		label: 'base.shared.requestType',
		component: <WorkplaceCaseTypeMultiSelectWithCheckList />,
	},
	[GridFilterItemType.WORKPLACES]: {
		key: 'workplaceIds',
		label: 'base.shared.workplace',
		component: <WorkplaceMultiSelectWithCheckList />,
	},
	[GridFilterItemType.WORKPLACE_PATIENT_STATUS]: {
		key: 'workplacePatientStatus',
		label: 'base.workplaceMemberStatus.status',
		component: (
			<Select
				firstUpperValues
				options={[
					{ value: null, label: 'base.everything' },
					{
						value: WorkplacePatientStatus.PENDING,
						label: 'base.inviteSended',
					},
					{
						value: WorkplacePatientStatus.VIRTUAL,
						label: 'base.waitingForInviteToMeddi',
					},
					{
						value: WorkplacePatientStatus.HEALTH_PLAN_ACTIVE,
						label: 'base.healthPlanActive',
					},
					{
						value: WorkplacePatientStatus.HEALTH_PLAN_CREATED,
						label: 'base.healthPlanCreated',
					},
					{
						value: WorkplacePatientStatus.HEALTH_PLAN_NOT_PAID,
						label: 'base.healthPlanNotPaid',
					},
					{
						value: WorkplacePatientStatus.ARCHIVED,
						label: 'base.archived',
					},
				]}
				translate
			/>
		),
	},
	[GridFilterItemType.HEALTH_PLAN_IDS]: {
		key: 'healthPlanIds',
		label: 'base.healthPlan',
		component: <HealthPlanMultiSelect />,
	},
	[GridFilterItemType.CASE_PRIORITY]: {
		key: 'priorityId',
		label: 'base.priority',
		component: <CasePrioritySelect />,
	},
	[GridFilterItemType.WORKPLACE]: {
		key: 'workplaceId',
		label: 'base.shared.workplace',
		component: <WorkplaceSelect />,
	},
	[GridFilterItemType.CASE_CREATE_DAY]: {
		key: 'createdAt',
		label: 'base.shared.date',
		component: (
			<Select
				options={[
					{ value: null, label: 'base.everything' },
					{
						value: 'BEFORE_1',
						label: 'base.olderThan1Day',
					},
					{
						value: 'BEFORE_3',
						label: 'base.olderThan3Day',
					},
					{
						value: 'BEFORE_7',
						label: 'base.olderThan7Day',
					},
					{
						value: 'DATE_RANGE',
						label: 'base.dateFromTo',
					},
				]}
			/>
		),
	},
	[GridFilterItemType.CASE_CODE]: {
		key: 'code',
		label: 'base.numberId',
		component: <TextField />,
	},
	[GridFilterItemType.ROOT_CASE_TYPE_CODE]: {
		key: 'caseTypeCode',
		label: 'base.shared.requestType',
		component: <RootCaseTypeSelect />,
	},
	[GridFilterItemType.MEDIC_ID_FROM_ALL_WORKPLACES]: {
		key: 'doctor',
		label: 'base.shared.doctor',
		component: (
			<DoctorSelect searchInAllWorkplaces roleKeys={['nurse', 'doctor']} />
		),
	},
	[GridFilterItemType.INVOICE_STATE]: {
		key: 'invoiceStatus',
		label: 'base.invoiceStatus',
		component: (
			<Select
				options={[
					{ value: 'PAID', label: 'base.invoicePaid' },

					{ value: 'CREATED', label: 'base.invoiceSent' },
				]}
				translate
			/>
		),
	},
	[GridFilterItemType.ANY_MEDIC_ID]: {
		key: 'medicId',
		label: 'base.paramedic',
		component: <DoctorSelect roleKeys={['doctor', 'therapist', 'nurse']} />,
	},
	[GridFilterItemType.SOLVED_AT]: {
		key: 'solvedAt',
		label: 'base.shared.date',
		component: (
			<Select
				options={[
					{ value: null, label: 'base.everything' },
					{
						value: 'BEFORE_1',
						label: 'base.lastMonth',
					},
					{
						value: 'BEFORE_3',
						label: 'base.lastThreeMonths',
					},
					{
						value: 'DATE_RANGE',
						label: 'base.dateFromTo',
					},
				]}
			/>
		),
	},
	[GridFilterItemType.NCZI_OWN_RECORDS]: {
		key: 'NCZIOwnRecords',
		label: 'base.shared.show',
		component: (
			<Select
				options={[
					{ value: false, label: 'base.everything' },
					{
						value: true,
						label: 'pouzé mé',
					},
				]}
			/>
		),
	},
	[GridFilterItemType.NCZI_RECORD_TYPE]: {
		key: 'NCZIRecordType',
		label: 'Typ vyšetření',
		component: (
			<MultiSelect
				options={[
					{
						value: NCZIElectronicExaminationRecordType.PROFESSIONAL_EXAMINATION,
						label: 'odborné vyšetření',
					},
					{
						value: NCZIElectronicExaminationRecordType.RELEASE_REPORT,
						label: 'propouštěcí zpráva',
					},
					{
						value: NCZIElectronicExaminationRecordType.IMAGING_EXAMINATION,
						label: 'zobrazovací vyšetření',
					},
				]}
			/>
		),
	},
	[GridFilterItemType.NCZI_SENSITIVITY]: {
		key: 'NCZISensitivity',
		label: 'Citlivost',
		component: (
			<Select
				options={[
					{
						value: 1,
						label: '1',
					},
					{
						value: 2,
						label: '2',
					},
					{
						value: 3,
						label: '3',
					},
					{
						value: 4,
						label: '4',
					},
					{
						value: 5,
						label: '5',
					},
				]}
			/>
		),
	},
	[GridFilterItemType.QUESTIONNAIRE_SEEN]: {
		key: 'questionnaireSeen',
		label: 'base.shared.show',
		component: (
			<Select
				options={[
					{ value: null, label: 'base.everything' },
					{
						value: 'ONLY_SEEN',
						label: 'base.onlySeen',
					},
					{
						value: 'ONLY_NOT_SEEN',
						label: 'base.onlyNotSeen',
					},
				]}
			/>
		),
	},
	[GridFilterItemType.FULLTEXT]: {
		key: 'fulltext',
		label: 'Fulltext',
		component: <TextField />,
	},
	[GridFilterItemType.ROLES]: {
		key: 'roleIds',
		label: 'component.workplaceAndWorkers.role',
		component: <RoleMultiSelectWithCheckList />,
	},
	[GridFilterItemType.ROLES_WITHOUT_ADMIN]: {
		key: 'roleIds',
		label: 'component.workplaceAndWorkers.role',
		component: <RoleMultiSelectWithCheckList excludeRoleKeys={['admin']} />,
	},
	[GridFilterItemType.INSURANCE_COMPANIES]: {
		key: 'insuranceCompanies',
		label: 'base.healthInsuranceCompany',
		component: <InsuranceCompaniesMultiSelectWithCheckList />,
	},
	[GridFilterItemType.FIELD_OF_CARE]: {
		key: 'fieldOfCare',
		label: 'component.caseType.filterFieldOfCare',
		component: <TextField />,
	},
	[GridFilterItemType.DENT_TOOTH_NUMBER]: {
		key: 'toothNumber',
		label: 'patientTreatment.meddiDENT.history.toothNumber',
		component: <TextField />,
	},
};

export const getGridFilterItemFormKey = (
	props: Partial<GridFilterItemProps>,
): string | undefined =>
	props.customKey ??
	(props.type
		? (ITEMS as Partial<Record<GridFilterItemType, { key: string }>>)[
				props.type
		  ]?.key
		: undefined);

const GridFilterItem: React.FC<GridFilterItemProps> = forwardRef<
	HTMLDivElement,
	GridFilterItemProps
>((props, ref) => {
	const {
		type,
		customLabel,
		width,
		customOnChange,
		customPlaceholder,
		customKey,
		visible = true,
		contentWidth,
		formControlClassName,
	} = props;
	const label = ITEMS[type].label;
	const key = ITEMS[type].key;
	const component = ITEMS[type].component;

	const handleChange = () => {
		customOnChange?.(props);
	};

	const wrapperStyle: React.CSSProperties = { position: 'relative' };

	if (width) {
		wrapperStyle.width = `${width}px`;
	}

	if (!visible) {
		return <></>;
	}

	return (
		<div ref={ref} style={wrapperStyle} className='filterInputWrapper'>
			<FormControl
				labelStyle={{ whiteSpace: 'nowrap' }}
				label={customLabel ?? label}
				name={customKey ?? key}
				onChange={handleChange}
				contentWidth={contentWidth}
				inLine
				hideRequired
				wrapperClassName={formControlClassName}>
				{React.cloneElement(component, {
					...component.props,
					placeholder: customPlaceholder,
				})}
			</FormControl>
		</div>
	);
});

export default GridFilterItem;
